import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, resolveComponent as _resolveComponent, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "margin-b-standard" }
const _hoisted_2 = { key: 0 }
const _hoisted_3 = {
  key: 1,
  class: "start-message"
}

import { mdiPlus, mdiPresentationPlay } from "@mdi/js";
import { computed, onMounted, PropType, ref, watch } from "vue";

import { useFilesStore } from "@/stores/files.store";
import { useUIFilesStore } from "@/stores/ui-files.store";

import { ContentStructureSection } from "@/models/content-structure.model";
import { LibraryFile } from "@/models/files.model";
import { SectionFile } from "@/models/product-section.model";

import FilesList from "@/components/right-sidebars/content-sidebar/authoring-tool/FilesList.vue";
import RightSidebarTitle from "@/components/right-sidebars/ui/RightSidebarTitle.vue";

type FileToDisplay = SectionFile | LibraryFile | UnavailableFile;
interface UnavailableFile {
  name: string;
  location: string;
  is_external: boolean;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'MediaContent',
  props: {
  section: {
    type: Object as PropType<ContentStructureSection>,
    required: true
  }
},
  setup(__props) {

const uiFilesStore = useUIFilesStore();
const filesStore = useFilesStore();

const props = __props;

const isFilesToDisplayLoading = ref<boolean>(true);
const filesToDisplay = ref<FileToDisplay[]>([]);
const getFileById = <T extends SectionFile | LibraryFile>(
  filesArray: T[]
): T | undefined => {
  // If a file was attached after the media content logic changes
  return filesArray.find((file: T): boolean => {
    return (
      file.id === props.section.authored_content.externalContent.attachmentId
    );
  });
};
const getFileByLocation = <T extends SectionFile | LibraryFile>(
  filesArray: T[]
): T | undefined => {
  // If a file was attached before the media content logic changes
  return filesArray.find((file: T): boolean => {
    return file.location === props.section.authored_content.externalContent;
  });
};
const getFilesToDisplay = (): void => {
  filesToDisplay.value = [];
  isFilesToDisplayLoading.value = true;

  const externalContent = props.section.authored_content?.externalContent;
  if (!externalContent) {
    isFilesToDisplayLoading.value = false;
    return;
  }

  const attachmentIdExists = Object.prototype.hasOwnProperty.call(
    externalContent,
    "attachmentId"
  );

  let currentFile: LibraryFile | SectionFile | undefined;

  // This is needed due to a change in the logic of setting a file as media content.
  // First, we look for whether our file is in the enabled files in a section,
  // so that we can detach the file from the section using the file drop-down menu
  // in the media content section
  if (attachmentIdExists) {
    currentFile = getFileById(props.section.enabled_files);
    // If a file is not in the enabled files of a section,
    // we look for it in the files library to get the necessary information about the file
    if (!currentFile) {
      currentFile = filesStore.files
        ? getFileById(filesStore.files)
        : undefined;
    }
  } else {
    // If the file was attached before the logic changes
    currentFile = getFileByLocation(props.section.enabled_files);
    // If a file is not in the enabled files of a section,
    // we look for it in the files library to get the necessary information about the file
    if (!currentFile) {
      currentFile = filesStore.files
        ? getFileByLocation(filesStore.files)
        : undefined;
    }
  }

  if (currentFile) {
    filesToDisplay.value.push(currentFile);
  } else {
    // Hide files deleted from the library
    if (attachmentIdExists) {
      isFilesToDisplayLoading.value = false;
      return;
    }
    // If the file is not in our file library and was attached before the media content logic changes
    filesToDisplay.value.push({
      name: props.section.authored_content.externalContent,
      location: props.section.authored_content.externalContent,
      is_external: true
    });
  }
  isFilesToDisplayLoading.value = false;
};
onMounted(() => {
  getFilesToDisplay();
});
watch(
  () => props.section,
  () => {
    getFilesToDisplay();
  }
);

const isStartMessageVisible = computed<boolean>(() => {
  return !isFilesToDisplayLoading.value && !filesToDisplay.value.length;
});
const openFileModal = (): void => {
  uiFilesStore.openAttachFileModal({
    section: props.section,
    shouldBeSetAsMediaContent: true
  });
};

return (_ctx: any,_cache: any) => {
  const _component_SVGIcon = _resolveComponent("SVGIcon")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(RightSidebarTitle, { title: 'Media Content' }),
    (filesToDisplay.value.length > 0)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createVNode(FilesList, {
            "files-to-display": filesToDisplay.value,
            section: __props.section,
            "is-attachments-table": false
          }, null, 8, ["files-to-display", "section"]),
          _createElementVNode("button", {
            class: "btn-3",
            type: "button",
            onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('previewAttachment', filesToDisplay.value[0].location)))
          }, [
            _createVNode(_component_SVGIcon, { path: _unref(mdiPresentationPlay) }, null, 8, ["path"]),
            _cache[1] || (_cache[1] = _createTextVNode(" Preview "))
          ])
        ]))
      : _createCommentVNode("", true),
    (isStartMessageVisible.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
          _cache[3] || (_cache[3] = _createElementVNode("p", null, "Add media content in one of the supported formats (upload/external link)", -1)),
          _createElementVNode("button", {
            class: "btn-3",
            type: "button",
            onClick: openFileModal
          }, [
            _createVNode(_component_SVGIcon, { path: _unref(mdiPlus) }, null, 8, ["path"]),
            _cache[2] || (_cache[2] = _createTextVNode(" Add Content "))
          ])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})